import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Compass, Plane, LayoutDashboard, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const NAV_ITEMS = [
  { href: '/',          icon: Home,            label: 'Home'      },
  { href: '/discover',  icon: Compass,         label: 'Discover'  },
  { href: '/routes',    icon: Plane,           label: 'Routes'    },
  { href: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
];

const MOBILE_BREAKPOINT = 768;

/**
 * Fixed bottom navigation bar — shown on mobile (< 768px) for signed-in users.
 * Uses JS-based mobile detection for reliable rendering.
 * Adds 'has-bottom-nav' to document.body so content scrolls above it.
 */
export default function MobileBottomNav() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT
  );

  // Track viewport width
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener('resize', check, { passive: true });
    return () => window.removeEventListener('resize', check);
  }, []);

  // Add/remove body class for bottom padding
  useEffect(() => {
    const active = !!user && isMobile;
    document.body.classList.toggle('has-bottom-nav', active);
    return () => document.body.classList.remove('has-bottom-nav');
  }, [user, isMobile]);

  if (!user || !isMobile) return null;

  const profileHref = `/profile/${user.user_id}`;
  const allItems = [...NAV_ITEMS, { href: profileHref, icon: User, label: 'Profile' }];

  const isActive = (href) => {
    if (href === '/') return pathname === '/';
    if (href.startsWith('/profile/')) return pathname.startsWith('/profile/');
    return pathname.startsWith(href);
  };

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50"
      data-testid="mobile-bottom-nav"
    >
      {/* Frosted glass floating pill */}
      <div className="mx-3 mb-3 rounded-2xl border border-border/80 bg-background/92 backdrop-blur-xl shadow-2xl shadow-black/20 overflow-hidden">
        <div className="flex items-center justify-around px-1 py-1.5">
          {allItems.map(({ href, icon: Icon, label }) => {
            const active = isActive(href);
            return (
              <Link
                key={href}
                to={href}
                className="flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-all min-w-0 flex-1"
                data-testid={`bottom-nav-${label.toLowerCase()}`}
              >
                <div className={`relative flex items-center justify-center w-9 h-9 rounded-xl transition-all duration-200 ${
                  active ? 'bg-[#FF4500]/12' : 'hover:bg-muted/60'
                }`}>
                  <Icon
                    className={`w-5 h-5 transition-all duration-200 ${
                      active ? 'text-[#FF4500]' : 'text-muted-foreground'
                    }`}
                    strokeWidth={active ? 2.5 : 1.8}
                  />
                  {active && (
                    <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-[#FF4500]" />
                  )}
                </div>
                <span className={`text-[10px] font-medium leading-none transition-colors ${
                  active ? 'text-[#FF4500]' : 'text-muted-foreground'
                }`}>
                  {label}
                </span>
              </Link>
            );
          })}
        </div>
        {/* iOS safe area */}
        <div style={{ height: 'env(safe-area-inset-bottom, 0px)' }} />
      </div>
    </nav>
  );
}
